import { ClientFactory } from "@/main/common/ClientFactory";
import { addPageActivityChangeListener, isDocumentVisible } from "@/main/common/PageActivity";

/**
 * Did this reader actually finish the post?
 *
 * `discussion_views` records that a page was OPENED and nothing else, so "opened" and "read" have been
 * the same observable since the beginning. This is the missing half.
 *
 * Design: `designs/01-now/2026-09-06-read-surface-completion-reading-time-and-author-stats-design.md` §2.
 *
 * ## The conjunction, and why neither half ships alone
 *
 * §2.1's correction: **visibility is not attention.** A long post scrolled to the bottom in two seconds
 * registers as fully seen; a phone in a pocket with the screen on registers as dwelling. Either signal
 * alone is trivially fooled, and a metric that is trivially fooled becomes a metric someone optimises.
 * So the beacon fires on the first moment BOTH hold — the end of the body has been reached, and the
 * page has been *visible* for some time — and neither half is reported on its own.
 *
 * The **dwell clock stops while the page is hidden**, which is what makes the second half mean
 * anything: a tab left open overnight would otherwise clear any threshold.
 *
 * ## The client reports; it does not decide
 *
 * There is no completion threshold in this file. The observed dwell and the observed length go on the
 * wire, and `ReadCompletionService.requiredDwellMillis` rules — deliberately, so that a bundle left in
 * the wild cannot pin what "completion" means to whatever it was built with. The one thing the client
 * decides is *when to ask*: waiting for the end of the body keeps a request off every page view on the
 * busiest page in the product, only to have it refused server-side.
 *
 * ## A separate object from `DiscussionViewTracker`, deliberately
 *
 * The design's requirement is that this "never blocks or delays the view call". Two objects that share
 * no code path satisfy that structurally rather than by care. They are started from the same component
 * and nothing passes between them.
 *
 * ## It degrades to silence, never to a false row
 *
 * No `IntersectionObserver`, no body node, or a transport failure all produce no beacon at all. A
 * completion that is not recorded costs an analytics row; a fabricated one costs the metric.
 */
export class DiscussionReadCompletionTracker {
    private readonly discussionPublicId: string;

    private observer: IntersectionObserver | null = null;
    private removePageActivityListener: (() => void) | null = null;

    /** Visible milliseconds banked before the current visible stretch began. */
    private bankedDwellMillis = 0;
    /** When the current visible stretch started, or `null` while the page is hidden. */
    private visibleSince: number | null = null;

    /** Once per page instance, ever. Latched before the request so a throw cannot un-latch it. */
    private reported = false;

    /**
     * Set by {@link stop}, and checked on the reporting path as well as guarding `start`.
     *
     * `disconnect()` is the contract, not the guarantee: an already-queued callback, a StrictMode
     * double-effect, or a stub in a test can still reach {@link reportOnce} after teardown. A beacon
     * that arrives after the component unmounted attributes a completion to a page the reader has
     * already left, which is a wrong row rather than a missing one.
     */
    private stopped = false;

    constructor(discussionPublicId: string) {
        this.discussionPublicId = discussionPublicId;
    }

    /**
     * Begins accumulating dwell and watching for the end of `bodyElement`.
     *
     * `bodyElement` is the node the post's HTML was injected into — not the `<article>`, which carries
     * the collapse `max-height` and would report its own clipped box rather than the body's end.
     */
    public start(bodyElement: HTMLElement | null): void {
        if (!this.discussionPublicId || !bodyElement || this.observer || this.stopped) {
            return;
        }

        this.visibleSince = isDocumentVisible() ? Date.now() : null;
        this.removePageActivityListener = addPageActivityChangeListener(() => {
            this.syncVisibility();
        });

        if (typeof IntersectionObserver === "undefined") {
            // An engine without it simply does not report completions. Reaching for a scroll listener
            // here would put a per-frame handler on the busiest page in the product to serve the
            // oldest browsers on it.
            return;
        }

        this.observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    this.reportOnce(bodyElement);
                }
            },
            // The END of the body: the observer fires when the element's bottom edge enters the
            // viewport, so the root margin trims the top three-quarters of the box out of
            // consideration and leaves the last slice as the trigger.
            { rootMargin: "0px 0px -25% 0px", threshold: 0 },
        );
        this.observer.observe(bodyElement);
    }

    public stop(): void {
        this.stopped = true;
        this.teardown();
    }

    /** Bank the stretch that just ended, or open a new one. */
    private syncVisibility(): void {
        const nowVisible = isDocumentVisible();
        if (nowVisible && this.visibleSince === null) {
            this.visibleSince = Date.now();
            return;
        }
        if (!nowVisible && this.visibleSince !== null) {
            this.bankedDwellMillis += Date.now() - this.visibleSince;
            this.visibleSince = null;
        }
    }

    private dwellMillis(): number {
        const openStretch = this.visibleSince === null ? 0 : Date.now() - this.visibleSince;
        return this.bankedDwellMillis + openStretch;
    }

    private reportOnce(bodyElement: HTMLElement): void {
        if (this.reported || this.stopped) {
            return;
        }
        this.reported = true;

        const dwellMillis = this.dwellMillis();
        // Plain text, never the HTML and never the body itself. `textContent` is what the server's
        // threshold is calibrated against, and it is the only thing about the body that leaves here.
        const bodyLength = bodyElement.textContent?.length ?? 0;

        // Torn down BEFORE the request: nothing about the answer changes what this tracker does next,
        // and a page that has reported is done watching.
        this.teardown();

        try {
            void Promise.resolve(
                ClientFactory.recordReadCompletion?.(this.discussionPublicId, { bodyLength, dwellMillis }),
            ).catch(() => {
                /* the member is reading; a beacon must never reach them */
            });
        } catch {
            /* a synchronous throw from the transport is the same non-event */
        }
    }

    private teardown(): void {
        this.observer?.disconnect();
        this.observer = null;
        this.removePageActivityListener?.();
        this.removePageActivityListener = null;
        this.visibleSince = null;
    }
}
